import { useInfiniteQuery, useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import apiHandler from '../services/apiServices';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { useSupabase } from '../contexts/SupabaseContext';
import { useState, useEffect } from 'react';

interface IntentSignal {
  id: string;
  keywords: string[];
  rulesets: string[];
  status?: string;
  // ... other properties
}

export const useIntentSignal = (itemsPerPage: number = 20, id?: string) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useSupabase();
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOption, setSortOption] = useState('created_at__desc');

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isFetchingSignals,
    error: fetchError,
    refetch
  } = useInfiniteQuery({
    queryKey: ['intentSignals', sortOption],
    queryFn: async ({ pageParam = 0 }) => {
      try {
        const skip = pageParam * itemsPerPage;
        const response = await apiHandler.get(
          `/intent-signals?skip=${skip}&limit=${itemsPerPage}&sort=${sortOption}`
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.length * itemsPerPage;
      return totalFetched < lastPage.total ? allPages.length : undefined;
    },
    initialPageParam: 0
  });

  const intentSignals = data?.pages.flatMap(page => page.data) || [];
  const totalCount = data?.pages[0]?.total || 0;

  // Send intent signal
  const sendIntentSignal = useMutation({
    mutationFn: async (signal: string) => {
      try {
        const response = await apiHandler.post('/intent-signals', { signal });
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['intentSignals'] });
      console.log('Intent signal sent successfully:', data);
    },
  });

  // Fetch single intent signal by ID without polling
  const { data: signalDetails, ...signalQueryRest } = useQuery<IntentSignal>({
    queryKey: ['intentSignal', id],
    queryFn: async () => {
      try {
        const response = await apiHandler.get(`/intent-signals/${id}`);
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!id,
  });

  // Add new mutation for updating intent signal
  const updateIntentSignal = useMutation({
    mutationFn: async ({ 
      id, 
      keywords, 
      rulesets,
      status 
    }: { 
      id: string; 
      keywords?: string[]; 
      rulesets?: string[];
      status?: string 
    }) => {
      try {
        const response = await apiHandler.patch(`/intent-signals/${id}`, {
          ...(keywords && { keywords }),
          ...(rulesets && { rulesets }),
          ...(status && { status })
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['intentSignals'] });
      queryClient.invalidateQueries({ queryKey: ['intentSignal', id] });
      console.log('Intent signal updated successfully:', data);
    },
  });

  const fetchAllSignals = async () => {
    try {
      const response = await apiHandler.get('/intent-signals?skip=0&limit=499');
      return response.data.data;
    } catch (error) {
      throw error;
    }
  };

  const fetchAllSignalsForSearch = async () => {
    try {
      const response = await apiHandler.get(`/intent-signals?skip=0&limit=${totalCount}`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  };

  return {
    intentSignals,
    totalCount,
    currentPage,
    setCurrentPage,
    isFetchingSignals,
    fetchError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    sendIntentSignal: sendIntentSignal.mutateAsync,
    isSending: sendIntentSignal.isPending,
    signalDetails,
    ...signalQueryRest,
    updateIntentSignal,
    fetchAllSignals,
    sortOption,
    setSortOption,
    refetch,
    fetchAllSignalsForSearch,
  };
};

export const useIntentSignalById = (signalId: string | undefined) => {
  return useQuery({
    queryKey: ['intentSignal', signalId],
    queryFn: async () => {
      if (!signalId) return null;
      const response = await apiHandler.get(`/intent-signals/${signalId}`);
      return response.data;
    },
    enabled: !!signalId,
    staleTime: Infinity
  });
}; 